import { gql } from '@apollo/client/core';

export const integrationEmailOptionFragment = gql`
  fragment IntegrationEmailOptionFragment on IntegrationEmailOption {
    type
    # Deprecated in favour of the three address lists, and no field on any tab
    # sets it. It is read for the same reason as the fax phone: supplierUpdate
    # replaces the whole array, and the bot user's email is still derived from
    # this key, so dropping it would repoint the bot user at the order address.
    emailAddress
    xlsx
    pdf
    complaintEmails
    orderEmails
    messageEmails
    __typename
  }
`;

export const integrationWhatsAppOptionFragment = gql`
  fragment IntegrationWhatsAppOptionFragment on IntegrationWhatsAppOption {
    type
    phone
    __typename
  }
`;

export const integrationSmsOptionFragment = gql`
  fragment IntegrationSmsOptionFragment on IntegrationSmsOption {
    type
    phone
    __typename
  }
`;

/**
 * Fax is deprecated and the form does not show it, but `supplierUpdate`
 * replaces the whole integration array. Without the phone here there is nothing
 * to send back, so any save on a supplier that still has a fax route would
 * delete it.
 */
export const integrationFaxOptionFragment = gql`
  fragment IntegrationFaxOptionFragment on IntegrationFaxOption {
    type
    phone
    __typename
  }
`;

export const pageInfoFragment = gql`
  fragment SupplierPageInfoFragment on PageInfo {
    endCursor
    hasNextPage
    hasPreviousPage
    startCursor
    __typename
  }
`;

export const supplierDetailsFragment = gql`
  fragment SupplierDetailsFragment on Supplier {
    id
    referenceId
    code
    externalName
    sortCartBy
    name
    description
    primaryAssortment
    secondaryAssortments
    contactEmails
    deliveryZones {
      name
      points {
        latitude
        longitude
        __typename
      }
      __typename
    }
    coverPhoto {
      url
      uploadUrl
      smallRectangle
      mediumRectangle
      largeRectangle
      __typename
    }
    locale
    isPuppy
    isTest
    isDiscoverable
    isIntegrated
    deliveryCosts
    minOrderAmount
    allowCustomProducts
    allowCustomerAcquisition
    allowOnlyOneActiveVariant
    allowBuyerComment
    allowInvoiceOrderGuidePhotoUpload
    enablePromotions
    enableCrossUnitPromotions
    enableTaxes
    pricingDateSource
    calculationPrecision
    deliveryDateMandatory
    customerNumberMandatory
    timezone
    hasEnabledCatalogProducts
    hasEnabledFractionalUnits
    hasValidCatalogProducts
    noDefaultCatalog
    defaultSubCatalog
    accessPackage
    disableCustomerNumberEdits
    restrictSalesRepDeliverySettingEdits
    allowOrderedProductMerging
    enableEcommerceProductGridView
    allowEcommercePoNumber
    priceSetting {
      displayInMarketplace
      displayToCustomers
      __typename
    }
    integration {
      ...IntegrationEmailOptionFragment
      ...IntegrationWhatsAppOptionFragment
      ...IntegrationSmsOptionFragment
      ...IntegrationFaxOptionFragment
      __typename
    }
    botUser {
      id
      profileImage {
        uploadUrl
        smallSquare
        mediumSquare
        largeSquare
        __typename
      }
      __typename
    }
    minOrder {
      price {
        currency
        amount
        __typename
      }
      strict
      __typename
    }
    hideMyPrice
    buyerVisibleStockStatuses
    __typename
  }
  ${integrationEmailOptionFragment}
  ${integrationWhatsAppOptionFragment}
  ${integrationSmsOptionFragment}
  ${integrationFaxOptionFragment}
`;

/**
 * Which eCommerce Advanced features a distributor has on.
 *
 * Served by catalog search rather than the supplier service, so it is spread
 * into the details query only, never into `SupplierDetailsFragment`: that
 * fragment also backs supplier search and the create and update responses, where
 * it would cost one catalog search call per distributor returned.
 *
 * Null for any caller who is neither a Choco admin nor one of the distributor's
 * own admins. A distributor nobody has changed reads as every feature off.
 */
export const supplierECommAdvancedSettingsFragment = gql`
  fragment SupplierECommAdvancedSettingsFragment on Supplier {
    eCommAdvancedSettings {
      searchSynonyms {
        enabled
        __typename
      }
      productBoosting {
        enabled
        __typename
      }
      __typename
    }
    __typename
  }
`;

export const searchSupplierEdgeFragment = gql`
  fragment SearchSupplierEdgeFragment on SupplierSearchEdge {
    node {
      ...SupplierDetailsFragment
      __typename
    }
    __typename
  }
  ${supplierDetailsFragment}
`;

export const supplierSearchConnectionFragment = gql`
  fragment SearchSupplierConnectionFragment on SearchSupplierConnection {
    total
    edges {
      ...SearchSupplierEdgeFragment
      __typename
    }
    pageInfo {
      ...SupplierPageInfoFragment
      __typename
    }
    __typename
  }
  ${searchSupplierEdgeFragment}
  ${pageInfoFragment}
`;
